import { useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { matchRoutes, useLocation, useNavigate, useParams } from "react-router";
import {
  PROJECTS_ROUTE,
  PROJECT_ROUTE,
  STORE_PACKAGE_ID,
  STORE_PROJECT_ID,
} from "Constants";
import { useGetProjectsSearchQuery } from "Services";

const useRestoreProjectsURL = (): void => {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId, projectId, packageId } = useParams();

  const { pathname } = location;

  const {
    data: groupData,
    isFetching,
    isLoading,
  } = useGetProjectsSearchQuery(
    groupId
      ? {
          groupId: groupId,
          searchName:
            "" /* we always want the full list of projects for this group */,
        }
      : skipToken,
  );

  // When you close a report, we navigate to /projects
  // RequireGroup then kicks in and starts working out which group to navigate to
  // isReadyToRestore will prevent *this* hook from triggering until that process is complete
  // which prevents 404 flashes and this being called more often than is necessary
  const readyRoute = [{ path: `/${PROJECTS_ROUTE}/group/:groupId` }];
  const hasLatestGroupData =
    groupData !== undefined && !isFetching && !isLoading;
  const isReadyToRestore =
    Boolean(matchRoutes(readyRoute, location)?.length) && hasLatestGroupData;

  useEffect(() => {
    // localStorage can get out of sync when more than 1 browser tab open using different groups,
    // so checked that any stored project ID is within group data before using. [SWAP-1848]
    const storedProjectId = localStorage.getItem(STORE_PROJECT_ID);
    if (!groupData || !storedProjectId) return;

    if (
      !groupData.projects.some(
        (project) => project.project_id === storedProjectId,
      )
    ) {
      localStorage.removeItem(STORE_PROJECT_ID);
      localStorage.removeItem(STORE_PACKAGE_ID);
    }
  }, [groupData]);

  useEffect(() => {
    const storedProjectId = localStorage.getItem(STORE_PROJECT_ID);
    const storedPackageId = localStorage.getItem(STORE_PACKAGE_ID);

    if (isReadyToRestore) {
      if (!projectId && !packageId && storedProjectId && storedPackageId) {
        navigate(
          `${PROJECT_ROUTE}/${storedProjectId}/package/${storedPackageId}`,
          { replace: true },
        );
      } else if (!projectId && storedProjectId) {
        navigate(`${PROJECT_ROUTE}/${storedProjectId}`, { replace: true });
      }
    }

    if (projectId) {
      localStorage.setItem(STORE_PROJECT_ID, projectId);
      if (packageId) {
        localStorage.setItem(STORE_PACKAGE_ID, packageId);
      } else {
        localStorage.removeItem(STORE_PACKAGE_ID);
      }
    }
  }, [pathname, navigate, projectId, packageId, isReadyToRestore]);
};

export default useRestoreProjectsURL;
